import { useState, useEffect } from "react";
import { buttonloginstyle } from "../help/Utils";
import linkIcon from "../assets/link.png";
import secure from "../assets/secure.png";
import "aos/dist/aos.css";
import { Link } from "react-router-dom";
import { FaClock } from "react-icons/fa";
import { FaCircleInfo } from "react-icons/fa6";
import Hints from "../components/Hints";
import apiClient from "../API"; // 👈 import axios instance

const MAIL_PROVIDERS = [
  {
    label: "Open Gmail",
    url: "https://mail.google.com/mail/u/0/#inbox",
    domains: ["gmail.com", "googlemail.com"],
  },
  {
    label: "Open Outlook",
    url: "https://outlook.live.com/mail/0/inbox",
    domains: ["outlook.com", "hotmail.com", "live.com", "msn.com"],
  },
  {
    label: "Open Yahoo Mail",
    url: "https://mail.yahoo.com/",
    domains: ["yahoo.com", "ymail.com", "rocketmail.com"],
  },
  {
    label: "Open iCloud Mail",
    url: "https://www.icloud.com/mail/",
    domains: ["icloud.com", "me.com", "mac.com"],
  },
  {
    label: "Open AOL Mail",
    url: "https://mail.aol.com/",
    domains: ["aol.com"],
  },
  {
    label: "Open Proton Mail",
    url: "https://mail.proton.me/",
    domains: ["proton.me", "protonmail.com"],
  },
  {
    label: "Open Zoho Mail",
    url: "https://mail.zoho.com/",
    domains: ["zoho.com", "zohomail.com"],
  },
];

const getMailProvider = (email) => {
  const domain = email?.split("@")[1]?.trim().toLowerCase();
  if (!domain) return null;
  return MAIL_PROVIDERS.find((provider) => provider.domains.includes(domain));
};

const ResetPasswordLink = () => {
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");
  const [cooldown, setCooldown] = useState(0); // seconds left to wait
  const [resetEmail, setResetEmail] = useState("");

  // countdown effect
  useEffect(() => {
    let timer;
    if (cooldown > 0) {
      timer = setInterval(() => {
        setCooldown((prev) => prev - 1);
      }, 1000);
    }
    return () => clearInterval(timer);
  }, [cooldown]);

  useEffect(() => {
    setResetEmail(localStorage.getItem("resetEmail") || "");
  }, []);

  const mailProvider = getMailProvider(resetEmail);

  // 👇 Resend function
  const handleResend = async () => {
    setError("");
    setSuccess("");

    const email = localStorage.getItem("resetEmail");

    if (!email) {
      setError("No email found. Please go back and enter your email again.");
      return;
    }

    try {
      setLoading(true);
      console.log("Resending reset request...");

      const res = await apiClient.post("/auth/forgot-password", {
        email: email.trim(),
      });

      console.log("Resend response:", res.data);
      setSuccess("Password reset link has been resent! Please check your inbox.");

      // start 3 minute cooldown
      setCooldown(180);
    } catch (err) {
      console.error("API error:", err);

      if (err.response) {
        if (err.response.status === 404) {
          setError("No account found with this email.");
        } else if (err.response.data?.message) {
          setError(err.response.data.message);
        } else {
          setError("Something went wrong. Please try again.");
        }
      } else {
        setError("Network error. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative login_main body_padding pb-[100px] ">
      <div className="text-center">
        <img src={linkIcon} alt="reset icon" className="mx-auto mb-3" />
      </div>
      <h1 className="mb-2 text-center">Password reset link received</h1>
      <p className="text-center mb-10 text-[18px]">
        Click the link in your email to reset your password <br /> securely
      </p>

      <form className="bg-white rounded-xl border border-gray-200 shadow-sm sm:p-[35px] p-5 w-full max-w-[90%] sm:max-w-[500px] mx-auto login_form">
        <div className="border border-[#E5E7EB] bg-[#F9FAFB] p-6 rounded-[12px] flex items-start mb-[25px]">
          <FaCircleInfo className="mt-1 me-4" />{" "}
          <div>
            <p className="text-[#374151] font-semibold text-[14px] leading-[20px] mb-2">
              What happens next:
            </p>
            <ul className="list-disc ms-3 text-[14px]">
              <li className="mb-1">Check your email inbox</li>
              <li className="mb-1">Click the secure reset link</li>
              <li className="mb-1">Create your new password</li>
              <li>Access your dashboard</li>
            </ul>
          </div>
        </div>

        {error && <p className="text-red-500 text-sm mb-3">{error}</p>}
        {success && <p className="text-green-600 text-sm mb-3">{success}</p>}

        {mailProvider && (
          <div className="mb-5">
            <a
              href={mailProvider.url}
              target="_blank"
              rel="noopener noreferrer"
              style={buttonloginstyle}
              className="block w-full text-center"
            >
              {mailProvider.label}
            </a>
          </div>
        )}

        <div className="pt-5 border-t border-gray-200">
          <p className="text-center font-semibold">Didn't receive the email?</p>
          <button
            type="button"
            onClick={handleResend}
            style={buttonloginstyle}
            disabled={loading || cooldown > 0}
            className="w-full mt-5 mb-5"
          >
            {loading
              ? "Resending..."
              : cooldown > 0
              ? `Resend available in ${Math.floor(cooldown / 60)}:${
                  (cooldown % 60).toString().padStart(2, "0")
                }`
              : "Resend Reset Email"}
          </button>
        </div>

        <div className="pt-5 border-t border-gray-200">
          <p className="text-center font-semibold">
            Back to{" "}
            <Link to="/" className="text-[#E76F51] font-bold">
              Login
            </Link>
          </p>
        </div>
      </form>

      <div className="mt-6 flex justify-center items-center ">
        <FaClock className="me-3" />
        Reset link expires in 24 hours
      </div>
      <div className="mt-2 flex justify-center items-center ">
        <img src={secure} alt="secure login" className="me-3" />
        Secure encryption protects your data
      </div>
      <Hints
        title="Having trouble?"
        message="Check your spam folder or contact our support team for assistance."
        linkText="Contact Support"
        email="support@visa.com"
      />
    </div>
  );
};

export default ResetPasswordLink;
